import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  Copy,
  Check,
  FileText,
  Layers,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Globe,
  ListChecks,
  AlertCircle,
  ArrowRight,
  CheckCircle,
  FileCode
} from 'lucide-react';
import { ContentPlanningProject, ContentPlanningPage, PageFinalOutline, PageOutlineSection, PageAeoFaq } from '../../types';
import { ContentPlanningPdfService } from '../../services/contentPlanningPdfService';

interface PageOutlineWorkstationProps {
  project: ContentPlanningProject;
  onSaveProject: (project: ContentPlanningProject) => void;
}

export const PageOutlineWorkstation: React.FC<PageOutlineWorkstationProps> = ({
  project,
  onSaveProject,
}) => {
  const pages = project.pages || [];
  const [selectedPageId, setSelectedPageId] = useState<string>(pages[0]?.id || '');
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [copiedCleanOutline, setCopiedCleanOutline] = useState(false);
  const [pastedOutlineJson, setPastedOutlineJson] = useState('');
  const [autoFillStatus, setAutoFillStatus] = useState<string | null>(null);

  // Accordion Expand/Collapse States
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [expandedFaqs, setExpandedFaqs] = useState<Record<string, boolean>>({});
  const [showRawJson, setShowRawJson] = useState(false);

  const selectedPage = pages.find(p => p.id === selectedPageId) || pages[0];

  useEffect(() => {
    setAutoFillStatus(null);
    if (selectedPage?.finalOutline) {
      try {
        setPastedOutlineJson(JSON.stringify({
          seoTitle: selectedPage.finalOutline.seoTitle,
          metaDescription: selectedPage.finalOutline.metaDescription,
          h1: selectedPage.finalOutline.h1,
          recommendedWordCount: selectedPage.finalOutline.recommendedWordCount,
          outlineSections: selectedPage.finalOutline.outlineSections,
          aeoFaqs: selectedPage.finalOutline.aeoFaqs,
          contentGaps: selectedPage.finalOutline.contentGaps,
          valueAdditions: selectedPage.finalOutline.valueAdditions,
        }, null, 2));

        // Default expand first 2 sections & first 2 FAQs
        const initialSecs: Record<string, boolean> = {};
        (selectedPage.finalOutline.outlineSections || []).forEach((sec, idx) => {
          initialSecs[sec.id || `sec-${idx}`] = idx < 2;
        });
        setExpandedSections(initialSecs);

        const initialFaqs: Record<string, boolean> = {};
        (selectedPage.finalOutline.aeoFaqs || []).forEach((faq, idx) => {
          initialFaqs[faq.id || `faq-${idx}`] = idx < 2;
        });
        setExpandedFaqs(initialFaqs);
        setShowRawJson(false);
      } catch {
        setPastedOutlineJson('');
        setShowRawJson(true);
      }
    } else {
      setPastedOutlineJson('');
      setShowRawJson(true);
    }
  }, [selectedPageId]);

  if (!selectedPage) {
    return (
      <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '48px', textAlign: 'center' }}>
        <FileText size={42} color="#334155" style={{ margin: '0 auto 16px auto' }} />
        <h3 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 700, margin: '0 0 8px 0' }}>
          No Pages Planned in this Project
        </h3>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          Please go back to Step 1 and add pages to your content plan first.
        </p>
      </div>
    );
  }

  // Update helper for current page
  const updateCurrentPage = (updatedPageFields: Partial<ContentPlanningPage>) => {
    const updatedPages = pages.map(p => {
      if (p.id === selectedPage.id) {
        return {
          ...p,
          ...updatedPageFields,
        };
      }
      return p;
    });

    onSaveProject({
      ...project,
      pages: updatedPages,
      updatedAt: new Date().toISOString(),
    });
  };

  // Dynamic research input counts for currently selected page ONLY (Req 3)
  const compHeadingsList = selectedPage.competitorHeadings || [];
  const totalCompHeadingsCount = compHeadingsList.flatMap(c => c.headings || []).length;
  const painPointsList = selectedPage.painPoints || [];
  const keywordCount = [selectedPage.primaryKeyword, selectedPage.secondaryKeyword, selectedPage.tertiaryKeyword].filter(Boolean).length;

  const combinedHeadingsData = selectedPage.combinedCompetitorHeadings?.join('\n') || (
    compHeadingsList
      .flatMap(c => c.headings || [])
      .filter(Boolean)
      .map(h => `- ${h}`)
      .join('\n') || 'None consolidated yet.'
  );

  const formattedCompetitorData = compHeadingsList.length === 0
    ? 'No competitor headings collected yet.'
    : compHeadingsList
        .filter(c => (c.headings && c.headings.length > 0) || c.nameOrUrl)
        .map(c => `Competitor #${c.competitorNumber} (${c.nameOrUrl || 'URL not specified'}):\n` + (c.headings || []).map(h => `  - ${h}`).join('\n'))
        .join('\n\n');

  const formattedPainPointsData = painPointsList.length === 0
    ? 'No customer pain points logged yet.'
    : painPointsList.map((p, i) => `${i + 1}. ${p.title}${p.description ? ': ' + p.description : ''}`).join('\n');

  const step3EeatPromptText = `# EEAT + COMMERCIAL INTENT SEO OUTLINE GENERATION PROMPT

## ROLE

Act as a **senior SEO content strategist, local SEO strategist, EEAT-focused content planner, and conversion-focused service-page strategist**.

Your task is to analyze the provided page information, keyword architecture, competitor research, customer pain points, business information, and research insights, then create a **comprehensive, original, EEAT-driven content strategy and webpage outline** for the selected page.

The outline will be used as the foundation for a later stage that generates the complete webpage.

The goal is NOT to create a purely commercial sales page and NOT to create a purely informational article.

The goal is to create a page that:

* Satisfies the user's search intent comprehensively
* Demonstrates genuine expertise and helpfulness
* Builds trust and credibility
* Addresses customer concerns and questions
* Provides useful information
* Establishes the business as a relevant solution
* Supports the visitor's purchasing decision
* Naturally moves commercially motivated visitors toward conversion

### CORE PRINCIPLE

**Commercial intent should guide the architecture, while helpful informational content, expertise, trust, local relevance, and customer education strengthen the page.**

The final outline should feel like the structure of a **high-quality expert service page**, not an SEO article with a CTA added at the end and not a sales pitch disguised as an article.

---

# 1. PRIMARY OBJECTIVE

Create a strategic blueprint for a webpage that serves users at different stages of the search and buying journey.

The page should primarily support users who may be considering purchasing the service, while also answering the informational questions they need answered before making that decision.

Think of the page as serving several overlapping intents:

### Commercial / Transactional Intent

The visitor may be:

* Comparing providers
* Looking for a service
* Evaluating whether to hire someone
* Trying to understand what the service includes
* Trying to understand pricing
* Looking for a trustworthy provider
* Ready to request an estimate, quote, consultation, booking, or other next step

### Informational Intent

The visitor may also want to understand:

* How the service works
* What the service includes
* What problems it can solve
* What affects the outcome
* What affects pricing
* What questions they should ask
* What they should expect from a professional provider
* Whether professional service is appropriate for their situation

### Trust / EEAT Intent

The visitor needs enough evidence and useful information to evaluate:

* Expertise
* Experience
* Professionalism
* Trustworthiness
* Transparency
* Relevance to their situation
* Local understanding where applicable

These intents should work together rather than being treated as completely separate content types.

---

# 2. CORE METHODOLOGY

Use the research provided below to follow this process:

1. Understand the page, business, location, primary keyword, keyword variations, and intended audience.

2. Determine the primary search intent and important secondary intents.

3. Analyze the supplied competitor H1, H2, and H3 headings collectively.

4. Determine what information competitors commonly cover.

5. Identify topics or information competitors under-cover, partially cover, or fail to address.

6. Identify customer questions, concerns, problems, buying motivations, objections, and decision-making needs.

7. Analyze the supplied business information to determine what can legitimately support trust, expertise, authority, and conversion.

8. Combine competitor research with keyword architecture, customer research, business information, and all other supplied research.

9. Identify genuine content gaps.

10. Identify meaningful value additions.

11. Determine what information the visitor needs before they can confidently make a decision.

12. Determine which topics are primarily commercial, informational, trust-building, or decision-supporting.

13. Organize those topics into a logical customer-focused journey.

14. Create an original H1-H3 outline that balances:

* Commercial intent
* Informational usefulness
* EEAT
* Customer experience
* Local relevance
* Search intent
* Conversion opportunities

### STRATEGIC FLOW

Use this broader methodology:

**Competitor Research**

↓

**Search Intent Analysis**

↓

**Customer Needs & Problems**

↓

**Buying Motivations & Objections**

↓

**EEAT / Trust Requirements**

↓

**Competitor Coverage**

↓

**Content Gaps**

↓

**Helpful Information & Value Additions**

↓

**Commercial Opportunities**

↓

**Customer Decision Journey**

↓

**Comprehensive EEAT + Commercial Outline**

---

# 3. SEARCH INTENT PRIORITIZATION

Determine the intent of the page before creating the outline.

Do NOT assume that every page is purely commercial simply because it is a service page.

Instead, determine the natural intent mix from the research.

For a service page, commercial intent will generally receive strong structural consideration, but informational content should remain where it genuinely helps users understand, evaluate, or confidently purchase the service.

### IMPORTANT

Do NOT force informational topics into the page merely to make it comprehensive.

Do NOT force commercial CTAs into informational sections where they would feel unnatural.

Do NOT eliminate useful informational content simply because it is not directly transactional.

The goal is **intent alignment**, not maximum commercial density.

---

# 4. COMMERCIAL INTENT AS THE ARCHITECTURAL BACKBONE

When choosing and ordering sections, prioritize information that helps a potential customer answer:

* Do I need this service?
* Is this the right service for my problem?
* Can this provider address my specific situation?
* What exactly will I receive?
* What should I expect?
* What might affect the cost?
* What concerns should I have?
* What should I look for in a provider?
* Why should I trust this business?
* What is my next step?

Commercial intent should therefore influence:

* Section order
* Section purpose
* Customer-facing headings
* Service explanations
* Objection handling
* Trust opportunities
* Pricing information
* CTA opportunities

However, **do not make every section explicitly sales-oriented**.

Some sections should exist primarily because they provide useful expertise or answer important questions.

---

# 5. EEAT REQUIREMENT

The outline must identify legitimate opportunities to demonstrate:

### Experience

Where supported, show relevant practical experience, real-world knowledge, processes, examples, or customer situations.

### Expertise

Include useful explanations that demonstrate understanding of the service and the problems customers face.

### Authoritativeness

Where verified business credentials, qualifications, certifications, associations, or relevant expertise are supplied, identify appropriate opportunities to communicate them.

### Trustworthiness

Address important trust barriers such as:

* Pricing transparency
* Service scope
* Communication
* Property protection
* Reliability
* Qualifications
* Insurance
* Guarantees
* Reviews
* Testimonials
* Policies
* Clear expectations

ONLY when supported by the supplied business information.

### IMPORTANT

Do not manufacture EEAT.

Never invent:

* Experience
* Credentials
* Certifications
* Licenses
* Awards
* Reviews
* Testimonials
* Case studies
* Customer numbers
* Guarantees
* Insurance
* Partnerships
* Results
* Processes
* Equipment
* Qualifications
* Industry memberships

If a potentially useful EEAT element is not provided, do not invent it.

Instead, the outline may identify a **trust opportunity** that can only be used if the provider supplies verified information.

---

# 6. CUSTOMER-FIRST OUTLINE LOGIC

The outline should reflect the customer's decision journey.

Consider the progression:

### Recognition

The visitor recognizes a problem or need.

### Relevance

They determine whether the service is appropriate for their situation.

### Understanding

They understand what the service includes and what they can expect.

### Evaluation

They evaluate quality, scope, pricing, process, expertise, and provider suitability.

### Trust

They determine whether the business appears credible and trustworthy.

### Objection Resolution

Important concerns are addressed.

### Decision

The visitor has enough information to choose whether to proceed.

### Conversion

The visitor has a clear and appropriate next step.

Not every page must contain a separate section for every stage.

The stages should influence the overall structure.

---

# 7. COMPETITOR RESEARCH

Use both the individual competitor headings and the combined competitor headings dataset.

Analyze competitors collectively rather than treating any single competitor as the template.

Look for:

* Common topics
* Common services
* Common customer concerns
* Common trust elements
* Common commercial sections
* Common informational sections
* Common FAQs
* Common conversion patterns
* Topics competitors emphasize
* Topics competitors under-cover
* Topics competitors omit
* Customer questions competitors do not adequately answer
* Useful information that could strengthen the page

Do not simply copy competitor headings into the final outline.

Create original headings that communicate the relevant topic naturally.

Do not reproduce a competitor's structure mechanically.

### IMPORTANT

Competitor content is **research evidence**, not a required checklist.

Do not attempt to include every topic found across competitors.

Prioritize topics based on:

1. Relevance
2. Customer value
3. Search intent
4. Commercial importance
5. EEAT value
6. Business relevance
7. Content gaps

---

# 8. KEYWORD ARCHITECTURE

The Primary / Seed Keyword is the central topic.

Secondary, tertiary, and additional keyword variations provide supporting topical context.

Use them to understand:

* Search demand variations
* Topic breadth
* Different ways users describe the service
* Supporting subtopics
* Search intent variations

Integrate keywords naturally into relevant sections.

Do not force every keyword into a heading.

Do not create sections solely to accommodate keywords.

Do not keyword stuff.

### IMPORTANT

The outline should be **topic-led and intent-led**, not keyword-led.

A section should exist because it provides value to the user, not because a keyword needs somewhere to appear.

---

# 9. CUSTOMER PAIN POINTS

Use the supplied customer pain points to determine what the page should address.

Pain points should influence:

* Section topics
* Section order
* Key points
* Objection handling
* FAQ questions
* Trust opportunities
* Conversion opportunities

Do not create a separate section for every pain point.

Instead, integrate related concerns into logical sections.

The eventual content should make the customer feel that the business understands the problem they are trying to solve.

---

# 10. CUSTOMER BUYING MOTIVATIONS

Identify why a customer would actually purchase the service.

Consider:

* Desired outcomes
* Convenience
* Quality
* Professional execution
* Risk reduction
* Time savings
* Appearance
* Maintenance
* Problem resolution
* Reliability
* Confidence
* Value

Only use motivations supported by the supplied research or naturally inherent to the service.

The outline should make space for **benefit-driven service positioning**, not merely feature descriptions.

---

# 11. CUSTOMER OBJECTIONS

Identify barriers that could prevent a visitor from purchasing.

These may include:

* Price concerns
* Unclear pricing
* Unclear service scope
* Trust concerns
* Quality concerns
* Reliability concerns
* Property damage concerns
* Difficulty reaching the property
* Uncertainty about results
* Uncertainty about what is included
* Concerns about scheduling
* Concerns about choosing the right provider

The outline should address meaningful objections in appropriate sections.

Do not create a separate objection section for every concern unless the research strongly supports it.

---

# 12. CONTENT GAPS

Identify genuine content gaps based on:

* Competitor research
* Customer research
* Search intent
* Business information
* EEAT requirements
* Decision-making needs

A content gap should represent useful information that is:

* Missing
* Under-covered
* Partially addressed
* Not clearly explained
* Important to the user's decision
* Important to solving the user's problem
* Important to establishing trust

Content gaps can include:

* Service-scope clarification
* Pricing considerations
* Important customer questions
* Trust information
* Decision-making guidance
* Relevant process information
* Important service distinctions
* Local considerations
* Practical explanations

Do not define generic SEO goals as content gaps.

For example, these are NOT content gaps:

* Improve SEO
* Add more keywords
* Increase topical authority
* Improve rankings
* Improve readability

---

# 13. VALUE ADDITIONS

Identify meaningful information that could add value beyond common competitor coverage.

Potential value additions may include:

* Expert explanations
* Practical guidance
* Customer decision-making information
* Relevant comparisons
* Service distinctions
* Useful process explanations
* Important considerations before purchasing
* Relevant local information
* Questions customers should consider
* Trust-building information
* Other useful information supported by research

Only include genuinely relevant additions.

Do not add content simply to make the page longer.

Do not invent information.

---

# 14. INFORMATIONAL CONTENT RULE

Informational content is encouraged when it strengthens the page.

However, informational content should have a legitimate reason to exist.

Ask:

> **Does this information help the visitor understand the problem, evaluate the service, make a better decision, or use the information meaningfully?**

If yes, include it where appropriate.

If it is merely generic educational material that could belong in a blog post and does not support the service-page intent, leave it out.

### DO NOT AUTOMATICALLY ADD:

* DIY tutorials
* Generic industry history
* Broad educational lessons
* Unrelated statistics
* Generic maintenance tips
* General industry definitions
* Topics included only because competitors have blog-style content

---

# 15. SERVICE-PAGE VS ARTICLE DISTINCTION

This is a service page.

The outline should NOT evolve into the structure of a long-form informational article.

Avoid structures dominated by:

* “What is...”
* “Everything you need to know...”
* “Complete guide...”
* “Top 10 tips...”
* “How to do it yourself...”
* “History of...”
* Broad educational sections

unless the research demonstrates that the topic is genuinely necessary for the page.

The page should remain anchored around:

**The service + the customer's need + the provider + trust + decision-making + conversion.**

---

# 16. LOCAL AND HYPER-LOCAL RELEVANCE

If the page targets a specific location, use relevant local or hyper-local information supplied in the research.

Where supported, consider:

* Local customer needs
* Local conditions
* Relevant areas
* Local market considerations
* Seasonal considerations
* Local service expectations
* Other useful location-specific information

Do not invent:

* Neighborhoods
* Landmarks
* Distances
* Statistics
* Local businesses
* Service areas
* Events
* Local claims

Only use local information supplied or supported by the available research.

---

# 17. SEMANTIC ENTITIES

Use relevant semantic entities supplied by the research where they improve topical completeness.

These may include:

* Products
* Services
* Brands
* Tools
* Processes
* Locations
* Industry concepts
* Businesses
* Other relevant entities

Only include genuinely relevant entities.

Do not add entities simply to increase entity count.

---

# 18. CONTENT CONSIDERATIONS

Consider whether the page needs relevant information such as:

* Human-focused introduction
* Customer problem
* Desired outcome
* Services
* Benefits
* Service scope
* Credibility and trust
* Local knowledge
* Customer questions
* Process or workflow
* Relevant equipment or tools
* Pricing considerations
* Reasons to choose the business
* Areas served
* Practical guidance
* Relevant comparisons
* Customer decision-making information
* Customer success information
* Conversion / CTA opportunities

These are considerations, not mandatory sections.

Include them only when relevant and supported.

### PRIORITY RULE

When deciding whether to include a topic, prioritize:

**Customer value + search intent + commercial relevance + EEAT + business relevance**

over simply increasing the amount of information.

---

# 19. TRUST AND BUSINESS INFORMATION

Where appropriate, identify legitimate opportunities to communicate credibility and trust.

Only use business information provided in the project data.

Never invent:

* Years of experience
* Certifications
* Licenses
* Awards
* Customer numbers
* Reviews
* Testimonials
* Case studies
* Partnerships
* Guarantees
* Prices
* Results
* Service areas
* Product availability
* Delivery times
* Other business-specific claims

Trust opportunities should be integrated naturally into the outline.

Do not automatically create a generic:

> “Why Choose Us”

section unless the research indicates that it is useful.

---

# 20. CONVERSION / CTA PLANNING

Conversion should be considered during outline creation, not added after the outline is finished.

For every major section, determine whether a CTA or conversion opportunity would naturally support the user's next step.

Possible conversion actions may include:

* Request an estimate
* Request a quote
* Contact the business
* Book a service
* Schedule a consultation
* Ask about availability
* Discuss the customer's property
* Learn what is included

Use only actions supported by the business information.

### IMPORTANT

Do NOT require an aggressive CTA after every section.

Instead, identify:

* Where a CTA is highly relevant
* Where a soft conversion opportunity is appropriate
* Where informational content should simply educate
* Where the customer naturally reaches a decision point

The final outline should have a clear conversion path without making every section sound like an advertisement.

---

# 21. SECTION CONVERSION ROLE

Every major section should have a clear purpose in the customer's journey.

In addition to \`purpose\` and \`keyPoints\`, include:

### \`conversionRole\`

This should explain how the section contributes to the overall buying journey.

Examples:

* Introduces the service and establishes relevance.
* Helps residential customers identify the service as appropriate for their needs.
* Clarifies service scope and reduces uncertainty.
* Addresses pricing concerns without inventing pricing.
* Demonstrates expertise while answering a customer concern.
* Builds trust using verified business information.
* Removes an objection preventing the customer from contacting the business.
* Answers a common question before purchase.
* Provides a natural transition toward requesting an estimate.

For sections that are primarily informational, the conversion role may simply explain how the information supports informed decision-making.

This is important:

**Not every section needs to sell. Every section needs a reason to exist.**

---

# 22. FAQ / AEO QUESTIONS

Create useful customer questions based on:

* Search intent
* Customer pain points
* Customer objections
* Competitor research
* Supplied FAQ research
* Important unanswered questions
* Pre-purchase decision-making needs

Prioritize questions that help users make a decision.

FAQs may include both:

### Informational questions

Questions that help the customer understand the service.

### Commercial questions

Questions that help the customer decide whether or how to purchase.

### Trust questions

Questions that reduce uncertainty about hiring the provider.

The number of questions should be determined by the topic.

Do not force a specific number.

Provide concise, direct answers using only supported information.

If there are no useful questions, return:

\`"aeoFaqs": []\`

---

# 23. SEO TITLE

Create a relevant, click-oriented SEO title based on:

* Primary keyword
* Page topic
* Search intent
* Location where appropriate
* Business context
* User value

Do not make ranking guarantees.

---

# 24. H1

Create a clear, user-focused H1 that accurately represents the page.

It should:

* Clearly identify the service
* Reflect the primary topic
* Reflect search intent
* Be natural
* Be compelling
* Support the page's commercial purpose without sounding overly promotional

---

# 25. META DESCRIPTION

Create a concise and relevant meta description that communicates the value of the page.

Where appropriate, communicate:

* Service
* Location
* Customer benefit
* Clear reason to visit

Do not make unsupported claims or guarantees.

---

# 26. RECOMMENDED WORD COUNT

Recommend an appropriate total word count based on:

* Page type
* Topic complexity
* Search intent
* Commercial intent
* Competitor coverage
* Content gaps
* Customer questions
* EEAT requirements
* Required useful information
* Level of comprehensiveness required

Do not assign arbitrary word counts to individual outline sections.

Do not inflate the recommended word count simply to make the page longer.

---

# 27. OUTLINE STRUCTURE

Create a logical H1-H3 hierarchy.

Use:

* H1 for the primary page topic
* H2 for major sections
* H3 for meaningful subtopics when additional hierarchy improves organization

The number of sections must be determined by:

* Search intent
* Customer needs
* Business purpose
* EEAT requirements
* Competitor coverage
* Content gaps
* Useful value additions
* Conversion journey

Do not add sections simply to make the outline longer.

Every section must have a clear purpose.

---

# 28. OUTLINE SECTION FORMAT

Each section must contain:

### \`headingTag\`

Use \`"H2"\` or \`"H3"\`.

### \`headingText\`

Write the actual proposed heading.

### \`purpose\`

Explain:

* What the section should accomplish
* What information it should cover
* Why it matters to the user
* Its role in the overall page

### \`conversionRole\`

Explain how the section contributes to:

* Commercial evaluation
* Trust
* Objection handling
* Decision-making
* Conversion

If the section is primarily informational, explain how the information supports the user's decision rather than forcing a sales purpose.

### \`keyPoints\`

List the specific:

* Information
* Questions
* Explanations
* Examples
* Customer concerns
* Benefits
* Trust elements
* Decision-making considerations

that should be covered.

---

# 29. HEADING QUALITY

Headings should be:

* Customer-focused
* Natural
* Specific
* Useful
* Original
* Relevant to the section

Where appropriate, headings may communicate:

* A customer problem
* A desired outcome
* A service
* A benefit
* A question
* A decision point

Avoid turning every heading into an advertisement.

For example, do not repeatedly use:

* “Why Choose Us”
* “Contact Us Today”
* “Get Started Today”
* “Best [Service]”
* “Professional [Service]”

unless genuinely appropriate.

---

# 30. AVOID OVER-OPTIMIZATION

Do not attempt to maximize:

* Keyword frequency
* Exact-match headings
* Commercial CTAs
* Sales language
* City-name repetition
* Semantic entities
* FAQs
* Section count

More optimization is NOT automatically better.

The page should feel like a genuinely useful expert-created service page.

A visitor should never feel:

> “This page was written for Google.”

They should feel:

> **“This page understands what I need and gives me enough useful information to decide what to do.”**

---

# 31. FACTUAL ACCURACY

Use only information supplied in the project data or supported by the provided research.

Do not fabricate:

* Statistics
* Dates
* Prices
* Business claims
* Customer stories
* Testimonials
* Case studies
* Credentials
* Locations
* Service areas
* Results
* Guarantees
* Equipment
* Processes
* Availability
* Partnerships
* Reviews
* Other factual information

If something is not supported, do not present it as a fact.

---

# 32. IMPORTANT BALANCE RULE

The final outline must balance four major objectives:

### 1. COMMERCIAL RELEVANCE

The visitor should be able to understand the service, evaluate it, overcome objections, and take action.

### 2. INFORMATIONAL VALUE

The visitor should receive genuinely useful answers and explanations.

### 3. EEAT

The page should create opportunities to demonstrate expertise, experience, authority, and trust using verified information.

### 4. NATURAL USER EXPERIENCE

The page should flow naturally and should not feel like a collection of SEO sections.

### PRIORITY

When these objectives conflict:

**User value and search intent come first.**

Commercial intent should strongly influence a service page, but it must not destroy helpfulness or trust.

---

# 33. FINAL STRATEGIC TEST

Before producing the outline, ask:

### Search Intent

* Does this structure satisfy the actual search intent?
* Does it serve both commercial and informational needs where appropriate?

### Commercial

* Can a potential customer understand the service?
* Are buying objections addressed?
* Are important decision factors covered?
* Is there a natural conversion path?

### EEAT

* Does the outline create legitimate opportunities to demonstrate expertise?
* Are trust elements included where supported?
* Does it avoid fabricated credibility?

### Helpful Content

* Does every section provide genuine value?
* Is informational content included when useful?
* Have unnecessary article-style topics been excluded?

### Customer Experience

* Does the page follow a logical decision journey?
* Does it answer the customer's real concerns?
* Does it make the service easier to evaluate?

### SEO

* Does the structure cover the primary topic comprehensively?
* Are keyword variations naturally accommodated?
* Are content gaps addressed?
* Is local relevance incorporated appropriately?

### Conversion

* Are CTAs placed where they make sense?
* Is the page persuasive without becoming repetitive?
* Does the final section provide a clear next step?

### Naturalness

* Would this feel like a genuine service page written by an expert?
* Does it avoid looking like an SEO content outline?
* Does it avoid becoming a generic “ultimate guide”?

---

# 34. OUTLINE ONLY

Do not write the finished webpage.

Do not write long-form paragraphs.

Do not attempt to reach the recommended word count within this response.

Do not write the complete article.

Do not write finished section copy.

The next content-generation stage will use this outline to produce the actual webpage.

The purpose of this stage is to create the **strategic blueprint**.

---

# 35. OUTPUT FORMAT

Return ONLY a valid JSON object.

Do not use Markdown.

Do not use code fences.

Do not include explanations before or after the JSON.

Use double quotes.

Do not include trailing commas.

Return exactly this structure:

{
"seoTitle": "...",
"metaDescription": "...",
"h1": "...",
"recommendedWordCount": 0,
"searchIntent": {
"primaryIntent": "...",
"secondaryIntents": [
"...",
"..."
]
},
"outlineSections": [
{
"headingTag": "H2",
"headingText": "...",
"purpose": "...",
"conversionRole": "...",
"keyPoints": [
"...",
"...",
"..."
]
}
],
"aeoFaqs": [
{
"question": "...",
"answer": "..."
}
],
"contentGaps": [
"...",
"..."
],
"valueAdditions": [
"...",
"..."
]
}

# 36. PROJECT DATA

## PAGE INFORMATION

Page Name:
${selectedPage.pageName}

Page Type:
${selectedPage.pageType || "Service Page"}

Page URL:
${selectedPage.targetUrl}

Targeted Location:
${project.targetedLocation || "Not specified"}

Business Category / Industry:
${project.category || "Not specified"}

## KEYWORD ARCHITECTURE

Primary / Seed Keyword:
${selectedPage.primaryKeyword || "Not specified"}

Primary Keyword Variations:
${selectedPage.primaryKeywordVariations && selectedPage.primaryKeywordVariations.length > 0 ? selectedPage.primaryKeywordVariations.join(", ") : "None"}

Secondary Keywords:
${selectedPage.secondaryKeyword || "Not specified"}

Secondary Keyword Variations:
${selectedPage.secondaryKeywordVariations && selectedPage.secondaryKeywordVariations.length > 0 ? selectedPage.secondaryKeywordVariations.join(", ") : "None"}

Tertiary Keywords:
${selectedPage.tertiaryKeyword || "Not specified"}

Tertiary Keyword Variations:
${selectedPage.tertiaryKeywordVariations && selectedPage.tertiaryKeywordVariations.length > 0 ? selectedPage.tertiaryKeywordVariations.join(", ") : "None"}

Additional Keyword Variations:
None

## COMPETITOR RESEARCH

Individual Competitor Headings:

${formattedCompetitorData}

Combined Competitor Headings Dataset:

${combinedHeadingsData}

## CUSTOMER RESEARCH

Customer Pain Points & Research Insights:

${formattedPainPointsData}

Customer Buying Motivations:

None

Customer Objections:

None

FAQ / Question Research:

None

## TOPICAL / LOCAL RESEARCH

Semantic Entities:

None

Local / Hyper-Local Information:

${project.targetedLocation || "None"}

Additional Research / Data:

None

## BUSINESS INFORMATION

Business Name:
${project.clientName}

Business Information:
${project.clientName} (${project.websiteUrl}) - ${project.category || "Business"}

Services:
${selectedPage.pageName}

Service Limitations:
None

Experience:
None

Qualifications:
None

Certifications:
None

Licensing:
None

Insurance:
None

Guarantees / Warranties:
None

Awards / Associations:
None

Service Areas:
${project.targetedLocation || "Not specified"}

Pricing:
None

Quote / Estimate Process:
None

Booking / Contact Method:
None

Primary CTA:
Contact Us / Request a Quote

Secondary CTA:
Call Us

Other Verified Trust Elements:
None

## APPROVED PAGE REQUIREMENTS

Approved Page Type:
${selectedPage.pageType || "Service Page"}

Approved Search Intent:
Commercial / Transactional Service Intent

Approved SEO Title:
${selectedPage.finalOutline?.seoTitle || "Not defined"}

Approved Meta Description:
${selectedPage.finalOutline?.metaDescription || "Not defined"}

Approved H1:
${selectedPage.finalOutline?.h1 || "Not defined"}

Approved Outline:
None

Target Word Count:
${selectedPage.finalOutline?.recommendedWordCount || "Not defined"}

# 37. FINAL INSTRUCTION

Create the most useful and strategically sound outline possible using the supplied research.

Do NOT blindly reproduce competitor structures.

Do NOT turn the page into a blog article.

Do NOT turn the page into an aggressive sales page.

Do NOT sacrifice informational usefulness for commercial intent.

Do NOT sacrifice commercial relevance for informational completeness.

Build a **balanced, expert-led, customer-focused service-page architecture** where:

**Commercial intent provides the backbone.**

**Informational content provides usefulness.**

**EEAT provides credibility.**

**Customer research provides relevance.**

**Competitor gaps provide differentiation.**

**SEO provides discoverability.**

**Conversion opportunities provide the path to action.**

The resulting outline should give the next content-generation stage enough direction to create a webpage that **ranks, helps, builds trust, and converts.**`;

  const masterPromptText = step3EeatPromptText;

  const handleCopyMasterPrompt = () => {
    navigator.clipboard.writeText(masterPromptText);
    setCopiedPrompt(true);
    setTimeout(() => setCopiedPrompt(false), 2500);
  };

  // Copy Clean Human-Readable Outline (Req 19)
  const handleCopyCleanOutline = () => {
    if (!selectedPage.finalOutline) return;
    const outline = selectedPage.finalOutline;

    const lines: string[] = [];
    lines.push(`CONTENT OUTLINE: ${selectedPage.pageName}`);
    lines.push(`Target URL: ${selectedPage.targetUrl}`);
    lines.push(`Primary Keyword: ${selectedPage.primaryKeyword || 'Not set'}`);
    lines.push(`Target Location: ${project.targetedLocation}\n`);

    lines.push(`H1: ${outline.h1}\n`);
    lines.push(`SEO TITLE: ${outline.seoTitle}`);
    lines.push(`META DESCRIPTION: ${outline.metaDescription}\n`);

    lines.push(`--- CONTENT OUTLINE ---`);
    (outline.outlineSections || []).forEach((sec, idx) => {
      const numStr = String(idx + 1).padStart(2, '0');
      const tag = sec.headingTag || 'H2';
      lines.push(`\n${tag} ${numStr}: ${sec.headingText}`);
      if (sec.purpose) {
        lines.push(`Purpose: ${sec.purpose}`);
      }
      if (sec.conversionRole) {
        lines.push(`Conversion Role: ${sec.conversionRole}`);
      }
      if (sec.keyPoints && sec.keyPoints.length > 0) {
        lines.push(`Key Points:`);
        sec.keyPoints.forEach(pt => lines.push(`  - ${pt}`));
      }
    });

    if (outline.contentGaps && outline.contentGaps.length > 0) {
      lines.push(`\n--- CONTENT GAPS ---`);
      outline.contentGaps.forEach((gap, idx) => {
        lines.push(`${String(idx + 1).padStart(2, '0')}. ${gap}`);
      });
    }

    if (outline.valueAdditions && outline.valueAdditions.length > 0) {
      lines.push(`\n--- VALUE ADDITIONS ---`);
      outline.valueAdditions.forEach(val => {
        lines.push(`- ${val}`);
      });
    }

    if (outline.aeoFaqs && outline.aeoFaqs.length > 0) {
      lines.push(`\n--- CUSTOMER QUESTIONS & FAQs ---`);
      outline.aeoFaqs.forEach(faq => {
        lines.push(`\nQ: ${faq.question}`);
        if (faq.answer) {
          lines.push(`A: ${faq.answer}`);
        }
      });
    }

    navigator.clipboard.writeText(lines.join('\n'));
    setCopiedCleanOutline(true);
    setTimeout(() => setCopiedCleanOutline(false), 2500);
  };

  // Parse JSON helper with robust fallback parser
  const parseAndSaveOutline = (rawText?: string, showFeedback = true): boolean => {
    const textToParse = typeof rawText === 'string' ? rawText : pastedOutlineJson;
    if (!textToParse || !textToParse.trim()) return false;

    try {
      let raw = textToParse.trim();

      // 1. Remove markdown code fences
      raw = raw.replace(/```[a-z]*\n?/gi, '').replace(/```/g, '').trim();

      // 2. Extract JSON payload
      const firstBrace = raw.indexOf('{');
      const lastBrace = raw.lastIndexOf('}');
      if (firstBrace !== -1 && lastBrace > firstBrace) {
        raw = raw.slice(firstBrace, lastBrace + 1);
      }

      // 3. Normalize curly quotes & clean trailing commas
      raw = raw.replace(/[\u201C\u201D]/g, '"').replace(/[\u2018\u2019]/g, "'");
      raw = raw.replace(/,\s*([\}\]])/g, '$1');

      const parsed = JSON.parse(raw);

      if (!parsed || typeof parsed !== 'object') {
        if (showFeedback) {
          setAutoFillStatus('⚠️ Invalid output structure. Please ensure output is a JSON object.');
        }
        return false;
      }

      const rawSections = parsed.outlineSections || parsed.sections || parsed.outline || parsed.outline_sections || parsed.headings || [];
      const rawFaqs = parsed.aeoFaqs || parsed.faqs || parsed.faq || parsed.aeo_faqs || parsed.questions || [];
      const rawGaps = parsed.contentGaps || parsed.gaps || parsed.marketGaps || parsed.content_gaps || parsed.market_gaps || [];
      const rawValues = parsed.valueAdditions || parsed.value_additions || parsed.valueAdds || parsed.additions || [];

      const sections: PageOutlineSection[] = rawSections.map((sec: any, idx: number) => ({
        id: `sec-${Date.now()}-${idx}-${Math.random().toString(36).substring(2, 6)}`,
        headingTag: sec.headingTag || sec.tag || 'H2',
        headingText: sec.headingText || sec.heading || sec.title || sec.name || `Section ${idx + 1}`,
        purpose: sec.purpose || sec.description || sec.summary || '',
        keyPoints: Array.isArray(sec.keyPoints) ? sec.keyPoints : (Array.isArray(sec.points) ? sec.points : []),
      }));

      const faqs: PageAeoFaq[] = rawFaqs.map((faq: any, idx: number) => ({
        id: `faq-${Date.now()}-${idx}-${Math.random().toString(36).substring(2, 6)}`,
        question: faq.question || faq.q || `Question ${idx + 1}`,
        answer: faq.answer || faq.a || '',
      }));

      const contentGaps: string[] = Array.isArray(rawGaps) ? rawGaps : [];
      const valueAdditions: string[] = Array.isArray(rawValues) ? rawValues : [];

      const finalOutline: PageFinalOutline = {
        seoTitle: parsed.seoTitle || parsed.seo_title || parsed.title || `${selectedPage.pageName} | ${project.clientName}`,
        metaDescription: parsed.metaDescription || parsed.meta_description || parsed.description || '',
        h1: parsed.h1 || parsed.h1Heading || parsed.h1_heading || selectedPage.pageName,
        recommendedWordCount: parsed.recommendedWordCount || parsed.word_count || parsed.recommended_word_count || parsed.wordCount || 2200,
        searchIntent: parsed.searchIntent || parsed.search_intent || undefined,
        outlineSections: sections,
        aeoFaqs: faqs,
        contentGaps,
        valueAdditions,
        notes: 'Auto-generated using AI Master Prompt Gap Analysis',
      };

      updateCurrentPage({
        finalOutline,
        status: 'completed',
      });

      if (showFeedback) {
        setAutoFillStatus('✅ Successfully parsed and saved Content Strategy & Outline!');
        setTimeout(() => setAutoFillStatus(null), 4000);
      }
      return true;
    } catch {
      if (showFeedback) {
        setAutoFillStatus('⚠️ JSON Parse Error. Unable to generate the outline. Your research has been preserved. Try again.');
      }
      return false;
    }
  };

  const handleAutoFillOutline = () => {
    parseAndSaveOutline(pastedOutlineJson, true);
  };

  const handleJsonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setPastedOutlineJson(val);
    if (val.trim()) {
      parseAndSaveOutline(val, false);
    }
  };

  const handleJsonPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = e.clipboardData.getData('text');
    if (pastedText && pastedText.trim()) {
      setPastedOutlineJson(pastedText);
      parseAndSaveOutline(pastedText, true);
    }
  };

  const finalOutline = selectedPage.finalOutline;

  // Dynamic calculations for Outline Summary (Req 16)
  const totalSectionsCount = (finalOutline?.outlineSections || []).length;
  const h2Count = (finalOutline?.outlineSections || []).filter(s => (s.headingTag || 'H2').toUpperCase() === 'H2').length;
  const h3Count = (finalOutline?.outlineSections || []).filter(s => (s.headingTag || 'H2').toUpperCase() === 'H3').length;
  const gapsCount = (finalOutline?.contentGaps || []).length;
  const valueAdditionsCount = (finalOutline?.valueAdditions || []).length;
  const faqsCount = (finalOutline?.aeoFaqs || []).length;

  // Toggle Section Expand State
  const toggleSection = (id: string) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAllSections = (expand: boolean) => {
    if (!finalOutline) return;
    const next: Record<string, boolean> = {};
    (finalOutline.outlineSections || []).forEach((sec, idx) => {
      next[sec.id || `sec-${idx}`] = expand;
    });
    setExpandedSections(next);
  };

  // Toggle FAQ Expand State
  const toggleFaq = (id: string) => {
    setExpandedFaqs(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="animate-fade-in" style={{ maxWidth: '1280px', margin: '0 auto' }}>
      
      {/* 1. PAGE SELECTOR TAB BAR */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '10px' }}>
          Select Planned Page for Content Strategy:
        </label>
        <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px' }}>
          {pages.map((p, idx) => {
            const isSelected = p.id === selectedPage.id;
            const hasOutline = !!p.finalOutline && p.finalOutline.outlineSections?.length > 0;

            return (
              <button
                key={p.id}
                onClick={() => setSelectedPageId(p.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 16px',
                  borderRadius: '12px',
                  border: isSelected ? '1.5px solid #0284c7' : '1px solid #1e293b',
                  background: isSelected ? 'rgba(2, 132, 199, 0.18)' : '#070c16',
                  color: isSelected ? '#ffffff' : '#94a3b8',
                  fontSize: '13px',
                  fontWeight: isSelected ? 700 : 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s ease',
                  flexShrink: 0,
                }}
              >
                <span>#{idx + 1} {p.pageName}</span>
                <span style={{ fontSize: '10.5px', background: hasOutline ? 'rgba(16, 185, 129, 0.2)' : 'rgba(245, 158, 11, 0.15)', color: hasOutline ? '#10b981' : '#f59e0b', padding: '2px 6px', borderRadius: '6px', fontWeight: 700 }}>
                  {hasOutline ? '✓ Outline Ready' : 'Needs Outline'}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 2. STEP 3 SCREEN TITLE & HEADER (Req 1 & Req 2) */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '8px' }}>
          <div>
            <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#ffffff', margin: '0 0 6px 0', letterSpacing: '-0.01em' }}>
              Content Strategy & Outline
            </h1>
            <p style={{ fontSize: '13.5px', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
              Analyze your keyword baseline and competitor research to identify content gaps, value additions, and the recommended structure for the page.
            </p>
          </div>
        </div>
      </div>

      {/* 3. TOP PAGE CONTEXT HEADER CARD (Req 2) */}
      <div
        style={{
          background: '#090e1a',
          border: '1px solid #1e293b',
          borderRadius: '16px',
          padding: '24px 28px',
          marginBottom: '20px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '3px 10px', borderRadius: '6px', textTransform: 'uppercase' }}>
                {selectedPage.pageType || 'Service Page'}
              </span>
              <span style={{ fontSize: '12.5px', color: '#94a3b8', fontFamily: 'var(--font-mono)' }}>
                {selectedPage.targetUrl}
              </span>
              {project.targetedLocation && (
                <span style={{ fontSize: '11.5px', color: '#cbd5e1', background: '#070c16', border: '1px solid #1e293b', padding: '2px 8px', borderRadius: '6px' }}>
                  Targeting: <strong>{project.targetedLocation}</strong>
                </span>
              )}
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#ffffff', margin: '0 0 6px 0', letterSpacing: '-0.01em' }}>
              {selectedPage.pageName}
            </h2>
            <div style={{ fontSize: '14px', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ color: '#94a3b8', fontWeight: 600 }}>Primary Keyword:</span>
              <span style={{ fontSize: '15px', fontWeight: 800, color: '#38bdf8', background: 'rgba(2, 132, 199, 0.15)', padding: '4px 12px', borderRadius: '8px', border: '1px solid rgba(56, 189, 248, 0.3)' }}>
                🎯 {selectedPage.primaryKeyword || selectedPage.pageName}
              </span>
            </div>
          </div>

          <div>
            <button
              onClick={() => setShowRawJson(!showRawJson)}
              style={{
                background: 'rgba(56, 189, 248, 0.12)',
                border: '1px solid rgba(56, 189, 248, 0.3)',
                color: '#38bdf8',
                fontSize: '12px',
                fontWeight: 700,
                padding: '8px 14px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <FileCode size={14} />
              <span>{showRawJson ? 'Hide Raw Prompt / JSON Input' : 'View Raw Prompt & JSON Input'}</span>
            </button>
          </div>
        </div>

        {/* 4. RESEARCH PIPELINE / INPUT SUMMARY BAR (Req 3) */}
        <div style={{ paddingTop: '16px', borderTop: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '8px' }}>
              RESEARCH INPUTS
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '12.5px' }}>
              <span style={{ background: '#070c16', border: '1px solid #1e293b', padding: '6px 12px', borderRadius: '8px', color: '#38bdf8', fontWeight: 700 }}>
                Keywords: <strong>{keywordCount}</strong>
              </span>
              <span style={{ background: '#070c16', border: '1px solid #1e293b', padding: '6px 12px', borderRadius: '8px', color: '#38bdf8', fontWeight: 700 }}>
                Competitors: <strong>{compHeadingsList.length}</strong>
              </span>
              <span style={{ background: '#070c16', border: '1px solid #1e293b', padding: '6px 12px', borderRadius: '8px', color: '#38bdf8', fontWeight: 700 }}>
                Headings: <strong>{totalCompHeadingsCount}</strong>
              </span>
              <span style={{ background: '#070c16', border: '1px solid #1e293b', padding: '6px 12px', borderRadius: '8px', color: '#f59e0b', fontWeight: 700 }}>
                Pain Points: <strong>{painPointsList.length}</strong>
              </span>
              <span style={{ background: '#070c16', border: '1px solid #1e293b', padding: '6px 12px', borderRadius: '8px', color: '#a855f7', fontWeight: 700 }}>
                Questions: <strong>{faqsCount}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 5. RESEARCH FLOW INDICATOR PIPELINE (Req 4) */}
      <div
        style={{
          background: '#070c16',
          border: '1px solid #1e293b',
          borderRadius: '14px',
          padding: '14px 20px',
          marginBottom: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
          STRATEGY PIPELINE
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '12px', fontWeight: 700 }}>
          <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CheckCircle size={14} /> Keyword Baseline
          </span>
          <ArrowRight size={14} color="#334155" />
          <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CheckCircle size={14} /> Competitor Research
          </span>
          <ArrowRight size={14} color="#334155" />
          <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <CheckCircle size={14} /> Gap Analysis
          </span>
          <ArrowRight size={14} color="#0284c7" />
          <span style={{ color: '#ffffff', background: '#0284c7', padding: '4px 12px', borderRadius: '6px', boxShadow: '0 2px 10px rgba(2, 132, 199, 0.4)' }}>
            ★ Content Strategy & Outline (Active)
          </span>
        </div>
      </div>

      {/* 6. COLLAPSIBLE MASTER AI PROMPT & RAW JSON (Req 18) */}
      {showRawJson && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '32px' }}>
          
          {/* LEFT BOX: MASTER AI PROMPT GENERATOR */}
          <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 800, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <Sparkles size={16} color="#38bdf8" />
                <span>Master AI Content Brief Prompt</span>
              </div>
              <button
                onClick={handleCopyMasterPrompt}
                style={{
                  background: copiedPrompt ? 'rgba(16, 185, 129, 0.15)' : 'rgba(2, 132, 199, 0.15)',
                  border: copiedPrompt ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(56, 189, 248, 0.3)',
                  color: copiedPrompt ? '#10b981' : '#38bdf8',
                  fontSize: '12px',
                  fontWeight: 800,
                  padding: '6px 14px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {copiedPrompt ? <Check size={14} /> : <Copy size={14} />}
                <span>{copiedPrompt ? 'Copied Master Prompt!' : 'Copy Master Prompt'}</span>
              </button>
            </div>

            <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: '0 0 12px 0' }}>
              This prompt automatically compiles your keywords, competitor headings, customer pain points, and market gap instructions.
            </p>

            <textarea
              readOnly
              rows={10}
              value={masterPromptText}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '10px',
                background: '#090e1a',
                border: '1px solid #1e293b',
                color: '#cbd5e1',
                fontSize: '11.5px',
                fontFamily: 'monospace',
                lineHeight: 1.5,
                resize: 'vertical',
              }}
            />
          </div>

          {/* RIGHT BOX: PASTE OUTPUT & AUTO-FILL */}
          <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '13.5px', fontWeight: 800, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px' }}>
                <Sparkles size={16} color="#f59e0b" />
                <span>Paste AI JSON Response</span>
              </div>

              <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: '0 0 12px 0' }}>
                Paste the returned JSON from ChatGPT/Gemini/Claude below for 1-click parsing.
              </p>

              <textarea
                rows={8}
                placeholder={`Paste JSON response here...\ne.g. {\n  "seoTitle": "${selectedPage.pageName} | ${project.clientName}",\n  "h1": "${selectedPage.primaryKeyword || selectedPage.pageName}",\n  "recommendedWordCount": 2200,\n  "outlineSections": [...]\n}`}
                value={pastedOutlineJson}
                onChange={handleJsonChange}
                onPaste={handleJsonPaste}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '10px',
                  background: '#090e1a',
                  border: '1px solid #1e293b',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  marginBottom: '14px',
                  resize: 'vertical',
                }}
              />
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={handleAutoFillOutline}
                    disabled={!pastedOutlineJson.trim()}
                    style={{
                      background: pastedOutlineJson.trim() ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : '#1e293b',
                      color: pastedOutlineJson.trim() ? '#070c16' : '#64748b',
                      fontSize: '13px',
                      fontWeight: 800,
                      padding: '10px 20px',
                      borderRadius: '10px',
                      border: 'none',
                      cursor: pastedOutlineJson.trim() ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Sparkles size={16} />
                    <span>Parse & Save Outline</span>
                  </button>

                  {pastedOutlineJson.trim() && (
                    <button
                      type="button"
                      onClick={() => {
                        setPastedOutlineJson('');
                        setAutoFillStatus('Cleared JSON input area');
                        setTimeout(() => setAutoFillStatus(null), 2500);
                      }}
                      style={{
                        background: 'transparent',
                        color: '#64748b',
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '10px 14px',
                        borderRadius: '10px',
                        border: '1px solid #1e293b',
                        cursor: 'pointer',
                      }}
                    >
                      Clear Text
                    </button>
                  )}
                </div>

                {autoFillStatus && (
                  <span style={{ fontSize: '12px', fontWeight: 700, color: autoFillStatus.startsWith('✅') ? '#10b981' : '#f59e0b' }}>
                    {autoFillStatus}
                  </span>
                )}
              </div>
            </div>
          </div>

        </div>
      )}

      {/* 7. MAIN PARSED OUTLINE WORKSTATION EXPERIENCE (Req 5 - Req 19) */}
      {finalOutline ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* WORKSTATION HEADER ACTIONS BAR */}
          <div
            style={{
              background: '#070c16',
              border: '1px solid #1e293b',
              borderRadius: '16px',
              padding: '18px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '14px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '11px', fontWeight: 800, color: '#10b981', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '3px 10px', borderRadius: '6px', textTransform: 'uppercase' }}>
                PARSED CONTENT OUTLINE & STRATEGY
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              {/* Copy Clean Outline Button (Req 19) */}
              <button
                type="button"
                onClick={handleCopyCleanOutline}
                style={{
                  background: copiedCleanOutline ? 'rgba(16, 185, 129, 0.15)' : 'rgba(56, 189, 248, 0.15)',
                  border: copiedCleanOutline ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(56, 189, 248, 0.3)',
                  color: copiedCleanOutline ? '#10b981' : '#38bdf8',
                  fontSize: '12.5px',
                  fontWeight: 800,
                  padding: '8px 16px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {copiedCleanOutline ? <Check size={14} /> : <Copy size={14} />}
                <span>{copiedCleanOutline ? 'Copied Clean Outline!' : 'Copy Clean Outline'}</span>
              </button>

              <button
                type="button"
                onClick={() => ContentPlanningPdfService.exportProjectPdfReport(project)}
                style={{
                  background: 'rgba(16, 185, 129, 0.15)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  color: '#10b981',
                  fontSize: '12.5px',
                  fontWeight: 800,
                  padding: '8px 16px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <FileText size={14} />
                <span>Export PDF Strategy Report</span>
              </button>
            </div>
          </div>

          {/* PAGE SEO STRATEGY CARD (Req 5 & Req 6) */}
          <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px' }}>
            <div style={{ fontSize: '12px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Globe size={16} />
              <span>Page SEO Strategy</span>
            </div>

            {/* H1 PROMINENT DISPLAY (Req 6) */}
            <div
              style={{
                background: '#090e1a',
                border: '1.5px solid rgba(16, 185, 129, 0.4)',
                borderRadius: '12px',
                padding: '18px 20px',
                marginBottom: '16px',
              }}
            >
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>
                H1
              </div>
              <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#ffffff', margin: 0, letterSpacing: '-0.01em', lineHeight: '1.3' }}>
                {finalOutline.h1 || selectedPage.pageName}
              </h1>
            </div>

            {/* SEO TITLE & RECOMMENDED LENGTH ROW (Req 5) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '6px' }}>
                  SEO TITLE
                </div>
                <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff' }}>
                  {finalOutline.seoTitle || selectedPage.pageName}
                </div>
              </div>

              <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '6px' }}>
                  RECOMMENDED LENGTH
                </div>
                <div style={{ fontSize: '18px', fontWeight: 800, color: '#38bdf8' }}>
                  ~{(finalOutline.recommendedWordCount || 2200).toLocaleString()} words
                </div>
              </div>
            </div>

            {/* META DESCRIPTION */}
            <div style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px', padding: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '6px' }}>
                META DESCRIPTION
              </div>
              <div style={{ fontSize: '13.5px', color: '#cbd5e1', lineHeight: '1.5' }}>
                {finalOutline.metaDescription || 'No meta description set.'}
              </div>
            </div>
          </div>

          {/* MAIN CONTENT OUTLINE SECTION (Req 7, 8, 9, 10, 11, 12) */}
          <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Layers size={18} color="#38bdf8" />
                  <span>CONTENT OUTLINE</span>
                </h3>
                <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: 0 }}>
                  {totalSectionsCount} Sections Planned ({h2Count} H2s, {h3Count} H3s)
                </p>
              </div>

              {/* Accordion Expand/Collapse All Buttons (Req 10) */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => toggleAllSections(true)}
                  style={{
                    background: '#090e1a',
                    border: '1px solid #1e293b',
                    color: '#cbd5e1',
                    fontSize: '12px',
                    fontWeight: 700,
                    padding: '6px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Expand All
                </button>
                <button
                  type="button"
                  onClick={() => toggleAllSections(false)}
                  style={{
                    background: '#090e1a',
                    border: '1px solid #1e293b',
                    color: '#94a3b8',
                    fontSize: '12px',
                    fontWeight: 700,
                    padding: '6px 12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Collapse All
                </button>
              </div>
            </div>

            {/* Dynamic Section Cards List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {(finalOutline.outlineSections || []).map((sec, idx) => {
                const secId = sec.id || `sec-${idx}`;
                const isExpanded = expandedSections[secId] !== false;
                const numStr = String(idx + 1).padStart(2, '0');
                const tag = (sec.headingTag || 'H2').toUpperCase();
                const isH3 = tag === 'H3';

                return (
                  <div
                    key={secId}
                    style={{
                      background: isH3 ? '#070c16' : '#090e1a',
                      border: isH3 ? '1px dashed #1e293b' : '1px solid #1e293b',
                      borderRadius: '12px',
                      marginLeft: isH3 ? '24px' : '0',
                      overflow: 'hidden',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {/* Section Card Header (Collapsible trigger) */}
                    <div
                      onClick={() => toggleSection(secId)}
                      style={{
                        padding: isH3 ? '12px 16px' : '16px 20px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '14px',
                        cursor: 'pointer',
                        background: isExpanded ? (isH3 ? 'rgba(168, 85, 247, 0.05)' : 'rgba(2, 132, 199, 0.04)') : (isH3 ? '#070c16' : '#090e1a'),
                        borderBottom: isExpanded ? '1px solid #1e293b' : 'none',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <span
                          style={{
                            fontSize: '11.5px',
                            fontWeight: 800,
                            color: isH3 ? '#a855f7' : '#38bdf8',
                            background: isH3 ? 'rgba(168, 85, 247, 0.15)' : 'rgba(2, 132, 199, 0.15)',
                            border: isH3 ? '1px solid rgba(168, 85, 247, 0.3)' : '1px solid rgba(56, 189, 248, 0.3)',
                            padding: '2px 8px',
                            borderRadius: '6px',
                            flexShrink: 0,
                          }}
                        >
                          {numStr} {tag}
                        </span>

                        <span
                          style={{
                            fontSize: isH3 ? '14.5px' : '16px',
                            fontWeight: isH3 ? 700 : 800,
                            color: '#ffffff',
                            letterSpacing: '-0.01em',
                          }}
                        >
                          {sec.headingText}
                        </span>
                      </div>

                      <div style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                          {sec.keyPoints?.length || 0} Key Points
                        </span>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </div>

                    {/* Section Card Body (Expanded) */}
                    {isExpanded && (
                      <div style={{ padding: isH3 ? '14px 16px' : '18px 20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {/* Section Purpose (Req 11) */}
                        {sec.purpose && (
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                              PURPOSE
                            </div>
                            <p style={{ fontSize: '13px', color: '#cbd5e1', margin: 0, lineHeight: '1.5' }}>
                              {sec.purpose}
                            </p>
                          </div>
                        )}

                        {/* Section Conversion Role */}
                        {sec.conversionRole && (
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                              CONVERSION ROLE
                            </div>
                            <p style={{ fontSize: '13px', color: '#fcd34d', margin: 0, lineHeight: '1.5' }}>
                              {sec.conversionRole}
                            </p>
                          </div>
                        )}

                        {/* Section Key Points (Clean Bullets - Req 12) */}
                        {sec.keyPoints && sec.keyPoints.length > 0 && (
                          <div>
                            <div style={{ fontSize: '11px', fontWeight: 800, color: isH3 ? '#a855f7' : '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                              KEY POINTS
                            </div>
                            <ul style={{ margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              {sec.keyPoints.map((pt, pIdx) => (
                                <li key={pIdx} style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: '1.4' }}>
                                  {pt}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* TWO-COLUMN PANELS FOR CONTENT GAPS & VALUE ADDITIONS (Req 13, 14, 17) */}
          {((finalOutline.contentGaps && finalOutline.contentGaps.length > 0) || (finalOutline.valueAdditions && finalOutline.valueAdditions.length > 0)) && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
              
              {/* CONTENT GAPS PANEL (Req 13) */}
              {finalOutline.contentGaps && finalOutline.contentGaps.length > 0 && (
                <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px' }}>
                  <h3 style={{ fontSize: '17px', fontWeight: 800, color: '#ffffff', margin: '0 0 14px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AlertCircle size={18} color="#10b981" />
                    <span>CONTENT GAPS</span>
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {finalOutline.contentGaps.map((gap, gIdx) => (
                      <div
                        key={gIdx}
                        style={{
                          background: '#090e1a',
                          border: '1px solid rgba(16, 185, 129, 0.25)',
                          borderRadius: '10px',
                          padding: '12px 16px',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '12px',
                        }}
                      >
                        <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 }}>
                          {String(gIdx + 1).padStart(2, '0')}
                        </span>
                        <span style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: '1.4' }}>
                          {gap}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* VALUE ADDITIONS PANEL (Req 14) */}
              {finalOutline.valueAdditions && finalOutline.valueAdditions.length > 0 && (
                <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px' }}>
                  <h3 style={{ fontSize: '17px', fontWeight: 800, color: '#ffffff', margin: '0 0 14px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ListChecks size={18} color="#38bdf8" />
                    <span>VALUE ADDITIONS</span>
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {finalOutline.valueAdditions.map((val, vIdx) => (
                      <div
                        key={vIdx}
                        style={{
                          background: '#090e1a',
                          border: '1px solid rgba(56, 189, 248, 0.25)',
                          borderRadius: '10px',
                          padding: '12px 16px',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '10px',
                        }}
                      >
                        <span style={{ fontSize: '14px', color: '#38bdf8', fontWeight: 800, flexShrink: 0 }}>
                          ✓
                        </span>
                        <span style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: '1.4' }}>
                          {val}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* CUSTOMER QUESTIONS & FAQS (Req 15) */}
          {finalOutline.aeoFaqs && finalOutline.aeoFaqs.length > 0 && (
            <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <HelpCircle size={18} color="#f59e0b" />
                    <span>Customer Questions & FAQs</span>
                  </h3>
                  <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: 0 }}>
                    {faqsCount} Questions Covered
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {finalOutline.aeoFaqs.map((faq, fIdx) => {
                  const faqId = faq.id || `faq-${fIdx}`;
                  const isFaqExpanded = expandedFaqs[faqId] !== false;

                  return (
                    <div
                      key={faqId}
                      style={{
                        background: '#090e1a',
                        border: '1px solid #1e293b',
                        borderRadius: '12px',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        onClick={() => toggleFaq(faqId)}
                        style={{
                          padding: '14px 18px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '12px',
                          cursor: 'pointer',
                          background: isFaqExpanded ? 'rgba(245, 158, 11, 0.04)' : '#090e1a',
                          borderBottom: isFaqExpanded && faq.answer ? '1px solid #1e293b' : 'none',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
                          <span style={{ fontSize: '11px', fontWeight: 800, color: '#f59e0b', background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.3)', padding: '2px 8px', borderRadius: '6px', flexShrink: 0 }}>
                            Q{String(fIdx + 1).padStart(2, '0')}
                          </span>
                          <span style={{ fontSize: '14.5px', fontWeight: 700, color: '#ffffff' }}>
                            {faq.question}
                          </span>
                        </div>
                        {faq.answer && (
                          <div style={{ color: '#64748b' }}>
                            {isFaqExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </div>
                        )}
                      </div>

                      {isFaqExpanded && faq.answer && (
                        <div style={{ padding: '14px 18px', fontSize: '13px', color: '#cbd5e1', lineHeight: '1.5' }}>
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* OUTLINE SUMMARY PANEL (Req 16) */}
          <div
            style={{
              background: '#070c16',
              border: '1px solid #1e293b',
              borderRadius: '16px',
              padding: '20px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px',
            }}
          >
            <div>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '4px' }}>
                OUTLINE SUMMARY
              </div>
              <div style={{ fontSize: '13px', color: '#cbd5e1' }}>
                Dynamic strategy metrics calculated for <strong style={{ color: '#ffffff' }}>{selectedPage.pageName}</strong>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '8px 14px', borderRadius: '10px', color: '#ffffff', fontWeight: 800, fontSize: '13px' }}>
                {totalSectionsCount} Sections
              </span>
              <span style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '8px 14px', borderRadius: '10px', color: '#38bdf8', fontWeight: 800, fontSize: '13px' }}>
                {h2Count} H2s
              </span>
              {h3Count > 0 && (
                <span style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '8px 14px', borderRadius: '10px', color: '#a855f7', fontWeight: 800, fontSize: '13px' }}>
                  {h3Count} H3s
                </span>
              )}
              {gapsCount > 0 && (
                <span style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '8px 14px', borderRadius: '10px', color: '#10b981', fontWeight: 800, fontSize: '13px' }}>
                  {gapsCount} Content Gaps
                </span>
              )}
              {valueAdditionsCount > 0 && (
                <span style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '8px 14px', borderRadius: '10px', color: '#38bdf8', fontWeight: 800, fontSize: '13px' }}>
                  {valueAdditionsCount} Value Additions
                </span>
              )}
              {faqsCount > 0 && (
                <span style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '8px 14px', borderRadius: '10px', color: '#f59e0b', fontWeight: 800, fontSize: '13px' }}>
                  {faqsCount} FAQs
                </span>
              )}
            </div>
          </div>

        </div>
      ) : (
        /* EMPTY STATE FOR UNPARSED OUTLINE (Req 22) */
        <div style={{ background: '#070c16', border: '1px border-dashed #1e293b', borderRadius: '16px', padding: '48px 24px', textAlign: 'center' }}>
          <Sparkles size={36} color="#f59e0b" style={{ margin: '0 auto 12px auto' }} />
          <h3 style={{ fontSize: '20px', color: '#ffffff', fontWeight: 800, margin: '0 0 8px 0' }}>
            CONTENT STRATEGY & OUTLINE
          </h3>
          <p style={{ fontSize: '14px', color: '#cbd5e1', margin: '0 0 16px 0', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
            Your research is ready. Generate an outline to analyze the competitor coverage, identify content gaps, and build the recommended page structure for <strong style={{ color: '#38bdf8' }}>{selectedPage.pageName}</strong>.
          </p>

          <div style={{ display: 'inline-flex', gap: '12px', background: '#090e1a', padding: '8px 16px', borderRadius: '10px', border: '1px solid #1e293b', fontSize: '12.5px', color: '#cbd5e1', marginBottom: '24px' }}>
            <span>Competitors: <strong style={{ color: '#38bdf8' }}>{compHeadingsList.length}</strong></span>
            <span>•</span>
            <span>Headings: <strong style={{ color: '#38bdf8' }}>{totalCompHeadingsCount}</strong></span>
            <span>•</span>
            <span>Pain Points: <strong style={{ color: '#f59e0b' }}>{painPointsList.length}</strong></span>
          </div>

          <div>
            <button
              onClick={() => setShowRawJson(true)}
              style={{
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                color: '#ffffff',
                fontSize: '13.5px',
                fontWeight: 800,
                padding: '12px 24px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 20px rgba(2, 132, 199, 0.35)',
              }}
            >
              <Sparkles size={16} />
              <span>Generate Outline / View AI Prompt</span>
            </button>
          </div>
        </div>
      )}

    </div>
  );
};
